/**
 * cart-nector.js
 * ─────────────────────────────────────────────────────────────────
 * Nector / PlumCash loyalty-coin redemption widget for the cart.
 * Depends on: cart-shared.js  (isWidgetEnabled, plumGetSharedCart,
 *                               plumBustSharedCart)
 * Depends on: window.nector_data  (populated by Nector's own script)
 * ─────────────────────────────────────────────────────────────────
 */
(function () {
  if (!isWidgetEnabled('nector_widget')) return;
  const CONFIG = {
    tiers: {
      welcome:    { coins_required: 1, starting_discount: 1, max_discount: 100,  minimum_cart_value: 0 },
      insider:    { coins_required: 1, starting_discount: 1, max_discount: 150,  minimum_cart_value: 0 },
      influencer: { coins_required: 1, starting_discount: 1, max_discount: 150,  minimum_cart_value: 0 },
      icon:       { coins_required: 1, starting_discount: 1, max_discount: 150,  minimum_cart_value: 0 }
    },
    defaultTier: 'welcome',
    selectors: {
      widget:                    '#charm-widget',
      checkbox:                  '#use-charm-checkbox',
      redeemText:                '#tier-charme',
      balanceText:               '#total-charm',
      cartDrawer:                '.cart-drawer, #cart-drawer',
      bagDiscount:               '#bag-summary-discount',
      bagTotal:                  '#bag-summary-total',
      bagSavings:                '#bag-summary-savings',
      checkoutBarTotal:          '#checkout-bar-total',
      checkoutBarOldTotal:       '#checkout-bar-old-total',
      checkoutBarSavePercentage: '#checkout-bar-save-percentage'
    }
  };

  let lastRenderKey = '';
  let syncTimer     = null;

  function scheduleSyncWidget() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncWidget, 110);
  }

  function $(sel) { return document.querySelector(sel); }

  function getElements() {
    const s = CONFIG.selectors;
    return {
      widget:                    $(s.widget),
      checkbox:                  $(s.checkbox),
      redeemText:                $(s.redeemText),
      balanceText:               $(s.balanceText),
      bagDiscount:               $(s.bagDiscount),
      bagTotal:                  $(s.bagTotal),
      bagSavings:                $(s.bagSavings),
      checkoutBarTotal:          $(s.checkoutBarTotal),
      checkoutBarOldTotal:       $(s.checkoutBarOldTotal),
      checkoutBarSavePercentage: $(s.checkoutBarSavePercentage)
    };
  }

  // ─── Money formatter ──────────────────────────────────────────────────────

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return '₹' + (Number(cents || 0) / 100).toFixed(2);
  }

  // ─── Nector data helpers ─────────────────────────────────────────────────

  function getNectorData() { return window.nector_data || null; }

  function getBalance(data) {
    const src = data || getNectorData();
    const raw = src?.lead?.wallet?.available ?? src?.wallet?.available ?? src?.lead?.available ?? 0;
    return Math.floor(Number(raw) || 0);
  }

  function getTier(data) {
    const src  = data || getNectorData();
    const tier = String(src?.lead?.tier || '').toLowerCase();
    return CONFIG.tiers[tier] ? tier : CONFIG.defaultTier;
  }

  function getTierConfig(data) { return CONFIG.tiers[getTier(data)]; }

  // ─── Cart helpers ─────────────────────────────────────────────────────────

  function getCart() {
    return new Promise(function (resolve) {
      window.plumGetSharedCart(function (cart) { resolve(cart); });
    });
  }

  function getCartValue(cart) { return Number(cart?.total_price || 0) / 100; }

  // ─── State calculation ────────────────────────────────────────────────────

  function calculateState(cartValue, data) {
    const balance    = getBalance(data);
    const tierConfig = getTierConfig(data);
    const result     = { balance, eligible_discount: 0, coins_needed: 0 };

    const cart             = Number(cartValue) || 0;
    const coinsRequired    = Number(tierConfig.coins_required)    || 0;
    const startingDiscount = Number(tierConfig.starting_discount) || 0;
    const maxDiscount      = Number(tierConfig.max_discount)      || 0;
    const minimumCartValue = Number(tierConfig.minimum_cart_value)|| 0;

    if (!coinsRequired || !startingDiscount) return result;
    if (cart < minimumCartValue)             return result;
    if (balance < coinsRequired)             return result;

    const discountFromBalance = Math.floor(balance / coinsRequired)                  * startingDiscount;
    const maxDiscountByTier   = Math.floor(maxDiscount / startingDiscount)           * startingDiscount;
    const maxDiscountByCart   = Math.floor(cart        / startingDiscount)           * startingDiscount;
    const eligibleDiscount    = Math.min(discountFromBalance, maxDiscountByTier, maxDiscountByCart);
    const discountUnits       = Math.floor(eligibleDiscount / startingDiscount);

    result.eligible_discount = eligibleDiscount;
    result.coins_needed      = discountUnits * coinsRequired;
    return result;
  }

  // ─── DOM total updates ────────────────────────────────────────────────────

  function updateTotalsFromHtml(charmDiscountRupees) {
    const els = getElements();

    function parseMoney(text) {
      if (!text) return 0;
      return Math.round(Number(String(text).replace(/[^0-9.-]/g, '')) * 100);
    }

    function getRowValue(label) {
      const row = Array.from(document.querySelectorAll('.bag-summary__row'))
        .find(function (el) { return el.children[0]?.textContent.trim() === label; });
      return row?.children[1] || null;
    }

    const bagTotalEl         = getRowValue('Bag total');
    const discountEl         = document.querySelector('#bag-summary-discount');
    const miniCartDiscountEl = document.querySelector('#mini-cart__discount');
    const shippingEl         = getRowValue('Shipping fee');
    const youPayEl           = document.querySelector('#bag-summary-total');
    const savingsEl          = document.querySelector('#bag-summary-savings');

    // Snapshot base values on first call — never read live textContent after mutation
    if (discountEl         && !discountEl.dataset.baseDiscount) {
      discountEl.dataset.baseDiscount = discountEl.textContent;
    }
    if (miniCartDiscountEl && !miniCartDiscountEl.dataset.baseMiniDiscount) {
      const span = miniCartDiscountEl.querySelector('.money');
      miniCartDiscountEl.dataset.baseMiniDiscount = span ? span.textContent : miniCartDiscountEl.textContent;
    }
    if (youPayEl  && !youPayEl.dataset.basePay) {
      youPayEl.dataset.basePay = youPayEl.textContent;
    }
    if (savingsEl && !savingsEl.dataset.baseSavings) {
      const span = savingsEl.querySelector('.money');
      savingsEl.dataset.baseSavings = span ? span.textContent : savingsEl.textContent;
    }

    const bagTotal         = parseMoney(bagTotalEl?.textContent);
    const baseDiscount     = parseMoney(discountEl?.dataset.baseDiscount             || '');
    const baseMiniDiscount = parseMoney(miniCartDiscountEl?.dataset.baseMiniDiscount || '');
    const baseYouPay       = parseMoney(youPayEl?.dataset.basePay                    || '');
    const baseSavings      = parseMoney(savingsEl?.dataset.baseSavings               || '');

    const shippingText = shippingEl?.textContent || '';
    const shipping     = /free/i.test(shippingText) ? 0 : parseMoney(shippingText);

    const llDiscount   = Math.round(Number(charmDiscountRupees || 0) * 100);
    const finalYouPay  = Math.max(0, baseYouPay - llDiscount);
    const finalSavings = baseSavings + baseMiniDiscount + llDiscount;

    if (els.bagDiscount) {
      els.bagDiscount.textContent = '- ' + formatMoney(baseDiscount);
    }

    // PlumCash row — insert or remove dynamically
    let llDiscountRow = document.querySelector('#ll-discount')?.closest('.bag-summary__row');

    if (llDiscount > 0) {
      if (!llDiscountRow) {
        const divider = document.querySelector('.bag-summary__divider');
        if (divider && divider.parentNode) {
          const row       = document.createElement('div');
          row.className   = 'bag-summary__row';
          row.innerHTML   = `
            <span>PlumCash</span>
            <span id="ll-discount" class="bag-summary__discount">-${formatMoney(llDiscount)}</span>`;
          divider.parentNode.insertBefore(row, divider);
          llDiscountRow = row;
        }
      } else {
        const llEl = llDiscountRow.querySelector('#ll-discount');
        if (llEl) llEl.textContent = '-' + formatMoney(llDiscount);
      }
    } else if (llDiscountRow) {
      llDiscountRow.remove();
    }

    if (els.bagTotal) els.bagTotal.textContent = formatMoney(finalYouPay);

    if (els.bagSavings) {
      const span = els.bagSavings.querySelector('.money');
      if (span) {
        span.textContent = formatMoney(finalSavings).replace(/^₹/, '');
      } else {
        els.bagSavings.textContent = formatMoney(finalSavings);
      }
    }

    if (els.checkoutBarTotal) {
      els.checkoutBarTotal.textContent = formatMoney(finalYouPay);
    }

    if (els.checkoutBarOldTotal && els.checkoutBarSavePercentage) {
      const compareAtTotal = bagTotal + shipping;
      const totalSaved     = Math.max(0, compareAtTotal - finalYouPay);
      const savePercentage = compareAtTotal > 0
        ? Math.round((totalSaved * 100) / compareAtTotal)
        : 0;

      if (totalSaved > 0) {
        els.checkoutBarOldTotal.style.display       = '';
        els.checkoutBarSavePercentage.style.display = '';
        els.checkoutBarOldTotal.textContent         = formatMoney(compareAtTotal);
        els.checkoutBarSavePercentage.textContent   = '(' + savePercentage + '% off)';
      } else {
        els.checkoutBarOldTotal.style.display       = 'none';
        els.checkoutBarSavePercentage.style.display = 'none';
      }
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  function render(cart) {
    const els       = getElements();
    if (!els.widget) return;

    const state     = calculateState(getCartValue(cart), getNectorData());
    const renderKey = [state.balance, state.coins_needed, state.eligible_discount].join('|');

    if (renderKey === lastRenderKey) {
      updateTotalsFromHtml(els.checkbox?.checked ? state.eligible_discount : 0);
      return;
    }

    lastRenderKey = renderKey;

    if (state.balance <= 0 || state.coins_needed <= 0 || state.eligible_discount <= 0) {
      els.widget.style.display = 'none';
      updateTotalsFromHtml(0);
      return;
    }

    els.widget.style.display = '';

    if (els.redeemText)  els.redeemText.textContent  = state.coins_needed;
    if (els.balanceText) els.balanceText.textContent  = state.balance;

    if (els.checkbox) {
      els.checkbox.disabled             = false;
      els.checkbox.dataset.coins        = String(state.coins_needed);
      els.checkbox.dataset.discount     = String(state.eligible_discount);
      els.checkbox.dataset.balance      = String(state.balance);
      els.checkbox.dataset.cartValue    = String(getCartValue(cart));
    }

    updateTotalsFromHtml(els.checkbox?.checked ? state.eligible_discount : 0);
  }

  // ─── Sync ─────────────────────────────────────────────────────────────────

  function syncWidget() {
    const widget = $(CONFIG.selectors.widget);
    if (!widget) return Promise.resolve();

    return getCart()
      .then(function (cart) { render(cart); })
      .catch(function () { widget.style.display = 'none'; });
  }

  // ─── Events ───────────────────────────────────────────────────────────────

  function bindEvents() {
    document.addEventListener('nector_lead_updated', function () {
      lastRenderKey = '';
      scheduleSyncWidget();
    });

    document.addEventListener('change', function (e) {
      const checkbox = e.target.closest(CONFIG.selectors.checkbox);
      if (!checkbox) return;

      const appliedDiscount = checkbox.checked
        ? Number(checkbox.dataset.discount || 0)
        : 0;

      updateTotalsFromHtml(appliedDiscount);

      window.dispatchEvent(new CustomEvent('nector:charm_toggle', {
        detail: {
          checked:    checkbox.checked,
          coins:      Number(checkbox.dataset.coins     || 0),
          discount:   appliedDiscount,
          balance:    Number(checkbox.dataset.balance   || 0),
          cart_value: Number(checkbox.dataset.cartValue || 0)
        }
      }));
    });
  }

  function observeCartDrawer() {
    const target = document.querySelector(CONFIG.selectors.cartDrawer) || document.body;
    new MutationObserver(function (mutations) {
      const drawerReplaced = mutations.some(function (m) {
        return Array.from(m.addedNodes).some(function (n) {
          return n.nodeType === 1 && (
            n.matches?.('cart-drawer')    ||
            n.querySelector?.('cart-drawer') ||
            n.id === 'charm-widget'       ||
            n.querySelector?.('#charm-widget')
          );
        });
      });
      if (!drawerReplaced) return;
      lastRenderKey = '';
      scheduleSyncWidget();
    }).observe(target, { childList: true, subtree: true });
  }

  function init() {
    bindEvents();
    observeCartDrawer();
    syncWidget();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  window.NectorCharmWidget = {
    refresh: function () {
      window.plumBustSharedCart();
      lastRenderKey = '';
      return syncWidget();
    },
    getState: function () {
      return getCart().then(function (cart) {
        return calculateState(getCartValue(cart), getNectorData());
      });
    }
  };
})();
